// src/pages/CreatorDashboard.jsx
import { useState, useEffect } from "react";
import API from "../services/api";
import CreatorWizard from "../components/CreatorWizard";
import MetricDrawer from "../components/MetricDrawer";
import CompareCreators from "../components/CompareCreators";
import MethodologyTab from "../components/MethodologyTab";
import CreatorDetails from "../components/CreatorDetails";  

import "./CreatorDashboard.css";

// ===== Helper Functions =====
import {
  getAcceleratingCreators,
  getBeautyGrowth,
  getUnderpricedCreators,
  getExplodingCreators,
} from "../services/intelHelpers"; // <-- put the helper functions in a file like src/services/intelHelpers.js

export default function CreatorDashboard() {
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [history, setHistory] = useState([]);
  const [spotlightDaily, setSpotlightDaily] = useState(null);
  const [spotlightWeekly, setSpotlightWeekly] = useState(null);
  const [topCreators, setTopCreators] = useState([]);

  const [loading, setLoading] = useState(true);
  const [editingCreator, setEditingCreator] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  const [requestUrl, setRequestUrl] = useState("");
  const [requestUsername, setRequestUsername] = useState("");
  const [metricCreators, setMetricCreators] = useState([]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const resCreators =  await API.get("/creators");
        const resHistory = await API.get("/history");
        const resSpotlight = await API.get("/history/spotlight");  
        const resTop = await API.get("/history/top_cy_score");


        console.log("Creators:", resCreators.data);
        console.log("History:", resHistory.data);
        console.log("Spotlight:", resSpotlight.data);
        console.log("Top Creators:", resTop.data);
        console.log("Top Creators (top_creators):", resTop.data.top_creators);

        setCreators(resCreators.data || []);
        setHistory(resHistory.data || []);
        setSpotlightDaily(resSpotlight.data.daily || null);
        setSpotlightWeekly(resSpotlight.data.weekly || null);
        setTopCreators(resTop.data.top_creators || []);

        

        // ✅ Only fetch CY Score if spotlight exists
        if (resSpotlight.data.daily) {
          const resCyScore = await API.get(
            `/creators/cy_score/${resSpotlight.data.daily.creator_id}`
          );
          console.log("Cy Score:", resCyScore.data);
        }
      } catch (err) {
        console.error("Failed to load creators", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ===== Intelligence Metrics =====
  const acceleratingCreators = getAcceleratingCreators(creators);
  const acceleratingCount = acceleratingCreators.length;

  const { growthPct, creators: beautyCreators } = getBeautyGrowth(creators, history);

  const underpricedCreators = getUnderpricedCreators(creators);
  const underpricedCount = underpricedCreators.length;

  const explodingCreators = getExplodingCreators(creators);
  const explodingCount = explodingCreators.length;


  const handleFinish = async () => {
    try {
      const res = await API.get("/creators");
      setCreators(res.data || []);
    } catch (err) {
      console.error("Failed to reload creators", err);
    }
    setEditingCreator(null);
  };

  if (loading) return <div className="container">Loading intelligence feed...</div>;

  return (
    <div className="container">
      <h1 className="title">🧠 Creator Intelligence Dashboard</h1>
      <p className="subtitle">Research creators in minutes, not hours.</p>
  
      <MetricDrawer
        creators={metricCreators}
        onClose={() => setMetricCreators([])}
        onEdit={(id) => setEditingCreator(id)}
        onDelete={(id) => console.log("Delete", id)}
      />

      {/* Tabs */}
      <div className="tabs">
        <button className={activeTab==="dashboard" ? "active" : ""} onClick={() => setActiveTab("dashboard")}>Overview</button>
        <button className={activeTab==="compare" ? "active" : ""} onClick={() => setActiveTab("compare")}>Compare Creators</button>
        <button className={activeTab==="research" ? "active" : ""} onClick={() => setActiveTab("research")}>Agency Requests</button>
        <button className={activeTab==="feedback" ? "active" : ""} onClick={() => setActiveTab("feedback")}> Agency Feedback</button>
        <button className={activeTab==="views" ? "active" : ""} onClick={() => setActiveTab("views")}>Views Ranking</button>
        <button className={activeTab==="methodology" ? "active" : ""} onClick={() => setActiveTab("methodology")}>Research Process</button>
      </div>

      {/* 1. Dashboard */}
      {activeTab === "dashboard" && (
        <div>
          <h2>Today’s Intelligence</h2>

          <section className="top-creators">
            <h2>Top 5 Creators by CY Score</h2>
            <div className="creator-nodes">
              {topCreators.map((c) => (
                <div key={c.creator_id} className="creator-node">
                  <span>@{c.username}</span>
                  <small>CY Score: {c.cy_score}</small>
                </div>
              ))}
            </div>
          </section>

          <div className="spotlight-row">
            {spotlightDaily && (
              <section className="creator-spotlight">
                <h2>Creator of the Day</h2>
                <h3>{spotlightDaily.creator_name}</h3>
                <p>Followers: {spotlightDaily.followers?.toLocaleString()}</p>
                <p>Momentum: {spotlightDaily.momentum}</p>
                <p>Virality: {spotlightDaily.virality}</p>
                <p>Commercial Score: {spotlightDaily.commercial_score}</p>
                <small>Updated {new Date(spotlightDaily.updated_at).toLocaleDateString()}</small>
              </section>
            )}

            {spotlightWeekly && (
              <section className="creator-spotlight">
                <h2>Creator of the Week</h2>
                <h3>{spotlightWeekly.creator_name}</h3>
                <p>Followers: {spotlightWeekly.followers?.toLocaleString()}</p>
                <p>Momentum: {spotlightWeekly.momentum}</p>
                <p>Virality: {spotlightWeekly.virality}</p>
                <p>Commercial Score: {spotlightWeekly.commercial_score}</p>
                <small>Updated {new Date(spotlightWeekly.updated_at).toLocaleDateString()}</small>
              </section>
            )}
          </div>


  
          {/* Section 1 – Today’s Intelligence */}
          <div className="intel-section">
            <h2>📊 Today’s Creator Market</h2>
            <p className="clickable-metric" onClick={() => setMetricCreators(acceleratingCreators)}>
            🔥 {acceleratingCount} creators accelerating faster than normal
            </p>

            <p className="clickable-metric" onClick={() => setMetricCreators(beautyCreators)}>
              📈 Beauty creators up {growthPct}%
            </p>

          </div>

          {/* Section 2 – Opportunity Board */}
          <div className="intel-section">
            <h2>🚀 Opportunities Today</h2>

            <p className="clickable-metric" onClick={() => setMetricCreators(underpricedCreators)}>
            💡  Underpriced creators ({underpricedCount}) 
            </p>
            
            <p className="clickable-metric" onClick={() => setMetricCreators(explodingCreators)}>
              💡  Exploding creators ({explodingCount}) 
            </p>

           
          </div>


        </div>   
      )}

      

      {/* 2. Research */}
      {activeTab === "research" && (
              <section className="agency-requests">
              <h2>Agency Research</h2>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const priority = e.target.priority.value;
      
                  try {
                    await API.post("/creators/agency_requests", {
                      tiktok_url: requestUrl,
                      priority,
                    });
                    alert(`Request submitted for ${requestUsername}! Our team will research manually.`);
                  } catch (err) {
                    console.error("Failed to submit request", err);
                    alert("You must be logged in to submit a request.");
                  }
                }}
              >
                <label>
                  Paste TikTok URL
                  <input
                    type="text"
                    name="tiktok_url"
                    value={requestUrl}
                    onChange={(e) => {
                      const url = e.target.value;
                      setRequestUrl(url);
      
                      // Extract username live
                      const extractUsername = (url) => {
                        if (url.includes("@")) {
                          return url.split("@").pop().split("/")[0];
                        }
                        const parts = url.replace(/\/$/, "").split("/");
                        return parts[parts.length - 1];
                      };
                      setRequestUsername(extractUsername(url));
                    }}
                    required
                  />
                </label>
      
                <label>
                  Extracted Username
                  <input
                    type="text"
                    name="username"
                    disabled
                    value={requestUsername}
                  />
                </label>
      
                <label>
                  Research Priority
                  <select name="priority">
                    <option value="normal">Normal</option>
                    <option value="rush">Rush (24hrs)</option>
                  </select>
                </label>
      
                <button type="submit">Submit</button>
              </form>
            </section>

      )}

      {/* 3. Feedback */}
      {activeTab === "feedback" && (
        <section className="agency-feedback">
          <h2>Tell us what to measure</h2>
          <p>Help Improve Creator Yard — what intelligence do you wish you had?</p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const question = e.target.question.value;
              try {
                await API.post("/creators/agency_feedback", { question });
                alert("Thank you! Your feedback has been submitted.");
                e.target.reset();
              } catch (err) {
                console.error("Failed to submit feedback", err);
                alert("You must be logged in to submit feedback.");
              }
            }}
          >
            <textarea name="question" rows="3" required />
            <button type="submit">Submit</button>
          </form>
        </section>
)}


      {/* 4. Compare */}
      {activeTab === "compare" && (
        <CompareCreators daily={spotlightDaily} weekly={spotlightWeekly} />
      )}

      {/* 5. Views ranking */}
      {activeTab === "views" && (
        <section className="views-compare">
          <h2>Creators Ranked by Views</h2>
          <table className="views-table">
            <thead>
              <tr>
                <th>Creator</th>
                <th>Followers</th>
                <th>Avg Views</th>
                <th>CY Score</th>
              </tr>
            </thead>
            <tbody>
              {creators
                .sort((a, b) => (b.avg_views || 0) - (a.avg_views || 0))
                .slice(0, 10) // top 10
                .map(c => (
                  <tr key={c.creator_id}
                    className="clickable-row"
                    onClick={() => setSelectedCreator(c)} // open details modal
                  >
                    <td>@{c.username || c.creator_name}</td>
                    <td>{c.followers?.toLocaleString()}</td>
                    <td style={{ color: "green", fontWeight: "bold" }}>
                      {c.avg_views?.toLocaleString()}
                    </td>
                    <td>{c.cy_score || "-"}</td>
                  </tr>
                ))}
            </tbody>
          </table>

          {selectedCreator && (
            <CreatorDetails
              creator={selectedCreator}
              onClose={() => setSelectedCreator(null)}
              onEdit={(c) => console.log("Edit", c)}
              onDelete={(id) => console.log("Delete", id)}
            />
        )}
        </section>
      )}

      {/* 6. methodology */}
      {activeTab === "methodology" && (
        <div>
        {/* Real-time CY score from spotlight */}
        {spotlightDaily || spotlightWeekly ? (
          <MethodologyTab creator={spotlightDaily || spotlightWeekly} />
        ) : (
          <p>No spotlight creator available.</p>
        )}

     
        </div>
      )}

      {/* Wizard Modal */}
      {editingCreator && (
        <div className="overlay">
          <div className="wizard-modal">
            <CreatorWizard creator={editingCreator} onFinish={handleFinish} />
            <button className="close-btn" onClick={() => setEditingCreator(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}


